/**
 * Client-side capability helpers. The server (`roles.ts`) is the single source of
 * truth for the role→capability matrix; the SPA never re-derives it. It only reads
 * the flat `capabilities[]` array the server returns from `/account/context` and
 * uses it to *show/hide* controls. Every gated action is still enforced server-side
 * — hiding a button is UX, not security (thin-client contract §9).
 */

import type { Capability, Role } from "../api/types";

export function can(capabilities: Capability[], cap: Capability): boolean {
  return capabilities.includes(cap);
}

/** Human labels for roles (display only). */
export const ROLE_LABEL: Record<Role, string> = {
  runner: "Runner",
  creator: "Creator",
  admin: "Admin",
  owner: "Owner",
};

/** Ordering used to detect a consequential *elevation* when changing a role. */
const ROLE_RANK: Record<Role, number> = { runner: 0, creator: 1, admin: 2, owner: 3 };

/**
 * Is moving `from` → `to` a consequential elevation (to admin/owner, or any
 * upward move)? Drives how heavy the confirmation is (§9: confirm proportional
 * to consequence). Elevating to a governance seat is the sharpest case.
 */
export function isConsequentialElevation(from: Role, to: Role): boolean {
  const up = ROLE_RANK[to] > ROLE_RANK[from];
  const toGovernance = to === "admin" || to === "owner";
  return up && toGovernance;
}

export function tierLabel(tier: string): string {
  return tier === "creator" ? "Creator (authoring + run)" : "Base (run-only)";
}
